// Floating dev panel — entity count, tick rate, determinism hash, optional render stats.
// Visible only in dev builds (toggled via `visible` prop).

import { useEffect, useState } from 'react';

import { cn } from './lib/utils.js';

export interface RenderStatsSnapshot {
  readonly drawCalls: number;
  readonly triangles: number;
  readonly geometries: number;
  readonly textures: number;
  readonly programs: number;
  readonly fps: number;
}

export interface DebugHudProps {
  readonly visible: boolean;
  readonly tick: bigint | number;
  readonly entityCount: number;
  readonly determinismHash: bigint | number;
  readonly fps?: number;
  readonly wsState?: 'connecting' | 'open' | 'closed' | 'errored';
  /** When provided, the HUD polls this getter at ~4Hz and renders draw-call / GPU stats. */
  readonly getRenderStats?: () => RenderStatsSnapshot;
  /** Positioning override; defaults to the top-right corner. */
  readonly className?: string;
}

export function DebugHud(props: DebugHudProps) {
  const [renderStats, setRenderStats] = useState<RenderStatsSnapshot | null>(null);

  // Poll the live stats object at a steady cadence. rAF would also work but
  // setState-throttling at ~4Hz is gentler on React and matches the HUD's purpose
  // (spot-checks during benchmarking, not real-time telemetry).
  useEffect(() => {
    if (!props.visible || !props.getRenderStats) {
      setRenderStats(null);
      return;
    }
    const get = props.getRenderStats;
    const tick = () => setRenderStats(get());
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [props.visible, props.getRenderStats]);

  if (!props.visible) return null;
  const tickStr = props.tick.toString();
  const hashStr = `0x${props.determinismHash.toString(16).padStart(16, '0')}`;
  return (
    <div
      aria-hidden
      data-testid="debug-hud"
      data-appdom-id="debug-hud"
      data-appdom-type="Custom"
      data-appdom-scope="aside"
      data-appdom-label="Debug HUD"
      className={cn(
        'pointer-events-none fixed top-14 right-3 z-50 min-w-[220px] rounded-lg border border-border/70 bg-card/92 px-3.5 py-2.5 font-mono text-xs text-card-foreground shadow-lg backdrop-blur-sm',
        props.className,
      )}
    >
      <Row label="tick">{tickStr}</Row>
      <Row label="entities">{props.entityCount}</Row>
      <Row label="hash">{hashStr.slice(0, 10)}…</Row>
      {renderStats ? (
        <Row label="fps">{renderStats.fps.toFixed(0)}</Row>
      ) : (
        props.fps !== undefined && <Row label="fps">{props.fps.toFixed(0)}</Row>
      )}
      {renderStats && (
        <>
          <Row label="draws">{renderStats.drawCalls.toLocaleString()}</Row>
          <Row label="tris">{formatTriangles(renderStats.triangles)}</Row>
          <Row label="geos">{renderStats.geometries.toLocaleString()}</Row>
          <Row label="texs">{renderStats.textures.toLocaleString()}</Row>
          <Row label="progs">{renderStats.programs.toLocaleString()}</Row>
        </>
      )}
      {props.wsState && (
        <Row label="ws">
          <span className={cn('font-medium', wsColorClass(props.wsState))}>{props.wsState}</span>
        </Row>
      )}
    </div>
  );
}

function formatTriangles(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 tabular-nums">
      <span className="text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function wsColorClass(state: NonNullable<DebugHudProps['wsState']>): string {
  switch (state) {
    case 'open':
      return 'text-emerald-400';
    case 'connecting':
      return 'text-amber-400';
    case 'closed':
      return 'text-muted-foreground';
    case 'errored':
      return 'text-destructive';
  }
}
